import React, { useState, useRef, useCallback, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamation,
  faCheck,
  faUpRightAndDownLeftFromCenter,
  faDownLeftAndUpRightToCenter,
  faUpload,
  faDownload,
  faCopy,
  faPaste
} from '@fortawesome/free-solid-svg-icons'
import AceEditor from 'react-ace'
import ace from 'brace'
import 'brace/theme/eclipse'
import 'brace/ext/language_tools'
import N3 from 'n3'
import { Tooltip } from '@mui/material'
import clsx from 'clsx'
import './ace-mode-turtle.js'
import style from './TurtleEditor.module.css'

// Custom Turtle completer
const turtleCompleter = {
  getCompletions: (
    _editor: any,
    _session: any,
    _pos: { row: number; column: number },
    prefix: string,
    callback: (error: any, completions: any[]) => void
  ) => {
    const keywords = [
      // Directives
      { value: '@prefix', meta: 'directive', score: 1000 },
      { value: '@base', meta: 'directive', score: 1000 },
      { value: 'PREFIX', meta: 'directive', score: 1000 },
      { value: 'BASE', meta: 'directive', score: 1000 },
      // Keywords
      { value: 'a', meta: 'keyword (rdf:type)', score: 900 },
      { value: 'true', meta: 'boolean', score: 800 },
      { value: 'false', meta: 'boolean', score: 800 },
      // Common prefixes
      { value: 'rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>', meta: 'prefix', score: 700 },
      { value: 'rdfs: <http://www.w3.org/2000/01/rdf-schema#>', meta: 'prefix', score: 700 },
      { value: 'xsd: <http://www.w3.org/2001/XMLSchema#>', meta: 'prefix', score: 700 },
      { value: 'owl: <http://www.w3.org/2002/07/owl#>', meta: 'prefix', score: 700 },
      { value: 'sh: <http://www.w3.org/ns/shacl#>', meta: 'prefix', score: 700 },
      { value: 'skos: <http://www.w3.org/2004/02/skos/core#>', meta: 'prefix', score: 700 },
      { value: 'dcterms: <http://purl.org/dc/terms/>', meta: 'prefix', score: 700 },
      { value: 'foaf: <http://xmlns.com/foaf/0.1/>', meta: 'prefix', score: 700 },
      // SHACL properties
      { value: 'sh:NodeShape', meta: 'shacl class', score: 600 },
      { value: 'sh:PropertyShape', meta: 'shacl class', score: 600 },
      { value: 'sh:targetClass', meta: 'shacl property', score: 600 },
      { value: 'sh:targetNode', meta: 'shacl property', score: 600 },
      { value: 'sh:property', meta: 'shacl property', score: 600 },
      { value: 'sh:path', meta: 'shacl property', score: 600 },
      { value: 'sh:name', meta: 'shacl property', score: 600 },
      { value: 'sh:description', meta: 'shacl property', score: 600 },
      { value: 'sh:datatype', meta: 'shacl property', score: 600 },
      { value: 'sh:minCount', meta: 'shacl property', score: 600 },
      { value: 'sh:maxCount', meta: 'shacl property', score: 600 },
      { value: 'sh:minLength', meta: 'shacl property', score: 600 },
      { value: 'sh:maxLength', meta: 'shacl property', score: 600 },
      { value: 'sh:pattern', meta: 'shacl property', score: 600 },
      { value: 'sh:class', meta: 'shacl property', score: 600 },
      { value: 'sh:nodeKind', meta: 'shacl property', score: 600 },
      { value: 'sh:in', meta: 'shacl property', score: 600 },
      { value: 'sh:order', meta: 'shacl property', score: 600 },
      { value: 'sh:group', meta: 'shacl property', score: 600 },
      { value: 'sh:defaultValue', meta: 'shacl property', score: 600 },
      { value: 'sh:IRI', meta: 'shacl nodeKind', score: 500 },
      { value: 'sh:BlankNode', meta: 'shacl nodeKind', score: 500 },
      { value: 'sh:Literal', meta: 'shacl nodeKind', score: 500 },
      // RDF/RDFS
      { value: 'rdf:type', meta: 'rdf property', score: 500 },
      { value: 'rdfs:label', meta: 'rdfs property', score: 500 },
      { value: 'rdfs:comment', meta: 'rdfs property', score: 500 },
      { value: 'rdfs:subClassOf', meta: 'rdfs property', score: 500 },
      { value: 'rdfs:subPropertyOf', meta: 'rdfs property', score: 500 },
      { value: 'rdfs:domain', meta: 'rdfs property', score: 500 },
      { value: 'rdfs:range', meta: 'rdfs property', score: 500 },
      // XSD datatypes
      { value: 'xsd:string', meta: 'datatype', score: 400 },
      { value: 'xsd:integer', meta: 'datatype', score: 400 },
      { value: 'xsd:decimal', meta: 'datatype', score: 400 },
      { value: 'xsd:boolean', meta: 'datatype', score: 400 },
      { value: 'xsd:date', meta: 'datatype', score: 400 },
      { value: 'xsd:dateTime', meta: 'datatype', score: 400 },
      { value: 'xsd:float', meta: 'datatype', score: 400 },
      { value: 'xsd:double', meta: 'datatype', score: 400 },
      { value: 'xsd:anyURI', meta: 'datatype', score: 400 },
    ]

    const completions = keywords
      .filter(k => k.value.toLowerCase().includes(prefix.toLowerCase()))
      .map(k => ({
        caption: k.value,
        value: k.value,
        meta: k.meta,
        score: k.score
      }))

    callback(null, completions)
  }
}

// Register the completer
const langTools = ace.acequire('ace/ext/language_tools')
if (langTools) {
  langTools.addCompleter(turtleCompleter)
}

interface TurtleEditorProps {
  name?: string
  exportFileName?: string
  value: string
  onChange?: (value: string) => void
  readOnly?: boolean
  placeholder?: string
  minHeight?: string
}

export const TurtleEditor: React.FC<TurtleEditorProps> = ({
  name,
  exportFileName,
  value,
  onChange,
  readOnly = false,
  placeholder = 'Enter Turtle syntax here...',
  minHeight = '300px'
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [validationStatus, setValidationStatus] = useState<string | 'ok' | null>(null)
  const [height, setHeight] = useState<number>(parseInt(minHeight) || 300)
  const [isDragging, setIsDragging] = useState(false)
  const startYRef = useRef(0)
  const startHeightRef = useRef(0)

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true)
    startYRef.current = e.clientY
    startHeightRef.current = height
    e.preventDefault()
  }, [height])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return

    const deltaY = e.clientY - startYRef.current
    const newHeight = Math.max(100, startHeightRef.current + deltaY)
    setHeight(newHeight)
  }, [isDragging])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Validate Turtle syntax using N3 Parser
  const checkTurtle = () => {
    try {
      const parser = new N3.Parser()
      parser.parse(value)
      setValidationStatus('ok')
    } catch (e) {
      setValidationStatus(String(e))
    }
  }

  // Format/prettify Turtle using N3 Parser and Writer
  const formatTurtle = () => {
    try {
      const parser = new N3.Parser()
      const quads = parser.parse(value)

      // Extract prefixes from the original text
      const prefixes: Record<string, string> = {}
      const prefixRegex = /@prefix\s+(\w*):?\s*<([^>]+)>\s*\./gi
      let match
      while ((match = prefixRegex.exec(value)) !== null) {
        prefixes[match[1]] = match[2]
      }

      const writer = new N3.Writer({ prefixes })
      writer.addQuads(quads)
      writer.end((error, result) => {
        if (error) {
          console.error('Error formatting Turtle:', error)
        } else {
          onChange?.(result)
        }
      })
    } catch (e) {
      // Can't format invalid Turtle
      console.error('Cannot format invalid Turtle:', e)
    }
  }

  // Export value as .ttl file
  const exportValueAsTurtle = () => {
    const blob = new Blob([value], { type: 'text/turtle' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = exportFileName || `${name || 'data'}.ttl`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Import value from .ttl file
  const importValueFromTurtle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target?.result
        if (text) {
          onChange?.(text as string)
        }
      }
      reader.readAsText(file)
    }
  }

  // Copy to clipboard
  const copyValueToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch (err) {
      console.error(err)
      alert('Failed to copy')
    }
  }

  // Paste from clipboard
  const pasteValueFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText()
      onChange?.(text)
    } catch (err) {
      console.error(err)
      alert('Failed to paste')
    }
  }

  const editorHeight = expanded ? '80vh' : `${height}px`

  return (
    <div className={style.wrapper}>
      <AceEditor
        key={String(expanded)}
        mode="turtle"
        theme="eclipse"
        value={value || ''}
        onChange={(newValue) => {
          if (readOnly) return
          setValidationStatus(null)
          onChange?.(newValue)
        }}
        readOnly={readOnly}
        name={name || `turtle-editor-${Math.random()}`}
        width="100%"
        height={editorHeight}
        fontSize={13}
        showPrintMargin={false}
        showGutter={true}
        highlightActiveLine={!readOnly}
        placeholder={placeholder}
        editorProps={{ $blockScrolling: Infinity }}
        setOptions={{
          showLineNumbers: true,
          tabSize: 2,
          useWorker: false,
          enableLiveAutocompletion: true,
          enableSnippets: true
        }}
        style={{
          border: '1px solid #ddd',
          borderRadius: '4px 4px 0 0',
        }}
      />
      {!expanded && (
        <div
          className={style.resizeWrapper}
          onMouseDown={handleMouseDown}
        >
          <div className={style.resizeChip} />
        </div>
      )}
      <div className={style.bar}>
        {!readOnly && (
          <Tooltip title="Validate Turtle syntax">
            <button className="px-1.5 py-px" type="button" onClick={() => checkTurtle()}>
              check
            </button>
          </Tooltip>
        )}
        {validationStatus && (
          <div className={clsx(style['turtle-status'], validationStatus !== 'ok' ? style.error : style.ok)}>
            <FontAwesomeIcon icon={validationStatus === 'ok' ? faCheck : faExclamation} />
            {validationStatus === 'ok' ? 'Valid Turtle' : String(validationStatus)}
          </div>
        )}
        <div className={style.separator} />
        {!readOnly && (
          <Tooltip title="Format">
            <button className="px-1.5 py-px" type="button" onClick={() => formatTurtle()}>
              <b>{`{ }`}</b>
            </button>
          </Tooltip>
        )}
        <Tooltip title="Save as .ttl">
          <button className="px-1.5 py-px" type="button" onClick={() => exportValueAsTurtle()}>
            <FontAwesomeIcon icon={faDownload} />
          </button>
        </Tooltip>
        {!readOnly && (
          <Tooltip title="Upload .ttl">
            <button className="px-1.5 py-px" type="button" onClick={() => inputRef.current?.click?.()}>
              <input
                ref={inputRef}
                type="file"
                accept=".ttl,.turtle"
                style={{ display: 'none' }}
                onChange={importValueFromTurtle}
              />
              <FontAwesomeIcon icon={faUpload} />
            </button>
          </Tooltip>
        )}
        <Tooltip title="Copy to clipboard">
          <button className="px-1.5 py-px" type="button" onClick={() => copyValueToClipboard()}>
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </Tooltip>
        {!readOnly && (
          <Tooltip title="Paste from clipboard">
            <button className="px-1.5 py-px" type="button" onClick={() => pasteValueFromClipboard()}>
              <FontAwesomeIcon icon={faPaste} />
            </button>
          </Tooltip>
        )}
        <Tooltip
          title={expanded ? 'Collapse' : 'Expand'}
          onClick={() => setExpanded(prevExpanded => !prevExpanded)}>
          <button className="px-1.5 py-px" type="button">
            <FontAwesomeIcon icon={expanded
              ? faDownLeftAndUpRightToCenter
              : faUpRightAndDownLeftFromCenter} />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}
